import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';

import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { MatSnackBar } from '@angular/material/snack-bar';
import { formHelper, regexHelper } from '@helpers';
import {
  AccountNotificationsInterface,
  apiHelpers,
  CollectionsService,
  ContactsInterface,
  ContactsService,
  NotificationsService,
  NotificationTypeEnum,
  SavedsearchesService,
  SearchCoordsResponse,
  SearchZipcodeResponse,
  SurveyItem,
  SurveysService,
  UserDataInterface,
  UserInterface,
  UserSettingsInterface,
  UsersService,
} from '@mzima-client/sdk';
import { TranslateService } from '@ngx-translate/core';
import { EnvService, SearchService } from '@services';
import { debounceTime, distinctUntilChanged, forkJoin } from 'rxjs';
import { ConfirmModalService } from '../../../core/services/confirm-modal.service';
import { DeleteContactModalComponent } from './delete-contact-modal/delete-contact-modal.component';
import { LocationSelectComponent } from './location-select/location-select.component';

enum AccountTypeEnum {
  Email = 'email',
  Phone = 'phone',
}

enum AlertLocationEnum {
  All = 'all',
  Radius = 'radius',
  None = 'none',
}

interface AccountTypeInterface {
  name: string;
  value: AccountTypeEnum;
}

@Component({
  selector: 'app-account-settings-modal',
  templateUrl: './account-settings-modal.component.html',
  styleUrls: ['./account-settings-modal.component.scss'],
})
export class AccountSettingsModalComponent implements OnInit {
  @ViewChild('updatePasswordFields') public updatePasswordFields: ElementRef;
  @ViewChild(LocationSelectComponent) locationSelectComponent!: LocationSelectComponent;
  public currentLocation = { lat: '', lng: '' }; // Default Location since Location is not Available
  public isLoadingZip = false;
  public form: FormGroup;
  public showMap: boolean = false;
  public emptyLocation = false;
  public profile: UserInterface;
  public contacts: ContactsInterface[];
  public surveys: SurveyItem[] = [];
  public isUpdatingPassword = false;
  public selectedTabIndex = 0;
  public isLoading: boolean;
  public isContactsChanged: boolean;
  public isAddAccountFormOpen: boolean;
  public matcher = new formHelper.FormErrorStateMatcher();
  public accountTypes: AccountTypeInterface[] = [
    {
      name: 'Email',
      value: AccountTypeEnum.Email,
    },
    {
      name: 'Phone',
      value: AccountTypeEnum.Phone,
    },
  ];
  public alertLocations: string[] = [
    AlertLocationEnum.All,
    AlertLocationEnum.Radius,
    AlertLocationEnum.None,
  ];
  public radiusOptions: number[] = [5, 10, 20];

  public profileForm: FormGroup;
  public addAccountForm: FormGroup;
  public alertsForm: FormGroup;
  public notifications: AccountNotificationsInterface[] = [];
  NotificationTypeEnum = NotificationTypeEnum;
  errorMap: { [key: string]: string | null } = {};
  public enableNotificationSettings = false;
  public authType = '';
  protected passwordAuthValidator: Validators;
  protected phoneAuthValidator: Validators;
  zipCountryCode = 'us';
  public alertSettings: {
    type: {
      meta?: UserSettingsInterface;
      value: string[];
    };
    location: {
      meta?: UserSettingsInterface;
      value: AlertLocationEnum;
    };
    radius: {
      meta?: UserSettingsInterface;
      value: number;
    };
  } = {
    type: {
      value: [],
    },
    location: {
      value: AlertLocationEnum.None,
    },
    radius: {
      value: 5,
    },
  };

  private checkPasswords: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
    if (!group) return null;
    const pass = group.get('password')?.value;
    const confirmPass = group.get('confirmPassword')?.value;
    if (confirmPass) {
      return pass === confirmPass ? null : { notSame: true };
    }
    return null;
  };

  constructor(
    private http: HttpClient,
    private usersService: UsersService,
    private contactsService: ContactsService,
    private surveysService: SurveysService,
    private formBuilder: FormBuilder,
    private confirmModalService: ConfirmModalService,
    private notificationsService: NotificationsService,
    private collectionsService: CollectionsService,
    private translate: TranslateService,
    private matDialogRef: MatDialogRef<AccountSettingsModalComponent>,
    private dialog: MatDialog,
    private _snackBar: MatSnackBar,
    private savedsearchesService: SavedsearchesService,
    private searchService: SearchService,
    private location: Location,
    private cdr: ChangeDetectorRef,
    private env: EnvService,
  ) {
    this.authType = this.env.environment.auth_type;
    this.passwordAuthValidator =
      this.authType === 'password' ? Validators.required : Validators.nullValidator;
    this.phoneAuthValidator =
      this.authType === 'phone' ? Validators.required : Validators.nullValidator;

    this.enableNotificationSettings = this.env.environment.enable_notification_settings;
    this.profileForm = this.formBuilder.group(
      {
        role: [''],
        display_name: ['', [this.passwordAuthValidator]],
        email: ['', [this.passwordAuthValidator, Validators.pattern(regexHelper.emailValidate())]],
        password: [''],
        confirmPassword: [''],
        zip_code: ['', [this.phoneAuthValidator]],
        phone: ['', [this.phoneAuthValidator]],
      },
      { validators: this.checkPasswords },
    );

    this.initAccountForm();
    this.getSurveys();
    this.initAlertsForm();
  }

  private initAccountForm() {
    this.addAccountForm = this.formBuilder.group({
      type: ['email', [this.passwordAuthValidator]],
      name: ['', [this.passwordAuthValidator, Validators.pattern(regexHelper.emailValidate())]],
    });
  }

  private getSurveys(): void {
    this.isLoading = true;
    this.surveysService.getSurveys('', { only: apiHelpers.ONLY.NAME_ID }).subscribe({
      next: (result) => {
        this.isLoading = false;
        this.surveys = result.results;
      },
    });
  }

  private initAlertsForm() {
    this.alertsForm = this.formBuilder.group({
      type: this.formBuilder.array(this.alertSettings.type.value || []),
      location: [this.alertSettings.location.value, [Validators.required]],
      radius: [this.alertSettings.radius.value, [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.getTabInformation();
    this.subscribeDisableSurveyCheckboxes();
    this.subscribeDisableRadiusRadioGroup();
    this.subscribeToZipCodeChanges();
  }

  private getProfile(): void {
    this.usersService.getCurrentUser().subscribe({
      next: (response) => {
        const { result } = response;
        this.profile = result;
        this.profileForm.patchValue({
          role: this.profile.role,
          display_name: this.profile.realname,
          email: this.profile.email,
          phone: this.profile.phone_number,
        });
        this.profileForm.markAsPristine();
        this.isLoading = false;
      },
    });
  }

  private getLocation(): void {
    this.usersService.getCurrentUser().subscribe({
      next: (response) => {
        const { result } = response;
        this.profile = result;
        this.profileForm.patchValue({
          zip_code: this.profile.zip_code,
        });
        switch (this.profile.country_code) {
          case 1:
            this.zipCountryCode = 'us';
            break;
          case 63:
            this.zipCountryCode = 'us';
            break;
        }
        this.getCoordsFromZipcode(this.profile.zip_code || '');
        this.isLoading = false;
        this.profileForm.markAsPristine();
      },
    });
  }
  private getContacts(): void {
    this.isContactsChanged = false;
    this.contactsService
      .get({
        user: 'me',
      })
      .subscribe({
        next: (response) => {
          this.contacts = response.results;
          this.isLoading = false;
        },
      });
  }

  public subscribeToZipCodeChanges() {
    this.profileForm
      .get('zip_code')
      ?.valueChanges.pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((data) => {
        this.onZipCodeChange(data);
      });
  }

  public onZipCodeChange(data: any) {
    if (this.currentLocation && !this.locationSelectComponent?.wasClicked) {
      this.getCoordsFromZipcode(data);
      this.cdr.detectChanges();
    }
  }

  public onManualZipCodeInput() {
    this.isLoadingZip = true;
    this.locationSelectComponent.wasClicked = false;
  }

  public toggleShowMap() {
    this.showMap = !this.showMap;
  }

  private getAlerts(): void {
    if (!this.profile) {
      this.getProfile();
    }
    if (!this.profile.id) return;

    if (this.profile.radius) {
      this.alertSettings.radius.value = this.profile.radius;
    }

    this.usersService.getAlertSettings(this.profile.id).subscribe({
      next: (response) => {
        const settings = response.results;
        const oldAlertType = settings.find((setting) => setting.config_key === 'alert_type');
        if (oldAlertType) {
          this.alertSettings.type.meta = oldAlertType;
          // '0' is a workaround for unassigned alert type / survey type
          this.alertSettings.type.value =
            oldAlertType.config_value === '0'
              ? []
              : oldAlertType.config_value.toString().split(',');
        }

        const checkboxes = this.surveys.map(
          (survey) => new FormControl(this.alertSettings.type.value.includes(survey.id.toString())),
        );

        this.alertsForm.setControl('type', this.formBuilder.array(checkboxes));

        const oldAlertLocation = settings.find(
          (setting) => setting.config_key === 'alert_location',
        );
        if (oldAlertLocation) {
          this.alertSettings.location.meta = oldAlertLocation;
          this.alertSettings.location.value = oldAlertLocation.config_value;
        }

        this.alertsForm.patchValue({
          location: this.alertSettings.location.value,
          radius: this.alertSettings.radius.value,
        });

        this.isLoading = false;
      },
    });
  }

  public subscribeDisableSurveyCheckboxes() {
    this.alertsForm.get('location')?.valueChanges.subscribe((data) => {
      if (data === 'none') {
        this.getAlertTypeFormArray().disable();
      } else {
        this.getAlertTypeFormArray().enable();
      }
    });
  }

  public subscribeDisableRadiusRadioGroup() {
    this.alertsForm.get('location')?.valueChanges.subscribe((data) => {
      if (data !== 'radius') {
        this.alertsForm.controls['radius'].disable();
      } else {
        this.alertsForm.controls['radius'].enable();
      }
    });
  }

  public getAlertTypeFormArray(): FormArray {
    return this.alertsForm.get('type') as FormArray;
  }

  public getAlertTypeCheckboxControls(index: number): FormControl {
    return this.getAlertTypeFormArray().controls[index] as FormControl;
  }

  public onAlertTypeCheckboxChange(event: MatCheckboxChange, index: number): void {
    const typeArray = this.getAlertTypeFormArray();
    typeArray.at(index).setValue(event.checked);
  }

  public updateProfile(): void {
    if (this.profileForm.invalid) return;

    let options: UserDataInterface = {
      realname: this.profileForm.value.display_name,
      email: this.profileForm.value.email,
    };
    if (this.isUpdatingPassword) {
      options = {
        ...options,
        password: this.profileForm.value.password,
      };
    }

    this.profileForm.disable();
    this.usersService.updateCurrentUser(options).subscribe({
      next: () => {
        this.getProfile();
        this.profileForm.controls['password'].setValue('');
        this.profileForm.controls['confirmPassword'].setValue('');
        this.updatePassword(false);
        this.profileForm.enable();
        this.closeModal();
      },
    });
  }

  public updateContacts(): void {
    forkJoin(
      this.contacts.map((contact) =>
        this.contactsService.update(contact.id, {
          contact: contact.contact,
          can_notify: contact.can_notify,
        }),
      ),
    ).subscribe({
      complete: () => {
        this.getContacts();
        this.closeModal();
      },
    });
  }

  public updateLocation(): void {
    if (this.profileForm.invalid) return;
    const options: UserDataInterface = {
      zip_code: this.profileForm.value.zip_code,
    };

    this.profileForm.disable();
    this.usersService.updateCurrentUser(options).subscribe({
      next: () => {
        this.getLocation();
        this.profileForm.enable();
        this.closeModal();
      },
    });
  }

  public updateAlerts(): void {
    const alertFormValues = this.alertsForm.getRawValue();
    const alertLocationParams = {
      config_key: this.alertSettings.location.meta?.config_key ?? 'alert_location',
      config_value: alertFormValues.location,
    };

    const alertTypeConfigValue = alertFormValues.type
      .reduce((acc: number[], value: boolean, index: number) => {
        if (value) {
          acc.push(this.surveys[index].id);
        }
        return acc;
      }, [])
      .join(',');

    const alertTypeParams = {
      config_key: this.alertSettings.type.meta?.config_key ?? 'alert_type',
      // workaround since blank strings are not accepted if no checkbox is selected
      config_value: alertTypeConfigValue === '' ? '0' : alertTypeConfigValue,
    };

    if (!this.profile.id) {
      return;
    }

    let alertLocationUpdate, alertTypeUpdate, radiusUpdate;

    if (!this.alertSettings.location.meta) {
      // No alertSettings meta / old value
      alertLocationUpdate = this.usersService.postUserSettings(
        this.profile.id.toString(),
        alertLocationParams,
      );
    } else {
      alertLocationUpdate = this.usersService.updateUserSettings(
        this.profile.id.toString(),
        alertLocationParams,
        this.alertSettings.location.meta.id,
      );
    }

    if (!this.alertSettings.type.meta) {
      alertTypeUpdate = this.usersService.postUserSettings(
        this.profile.id.toString(),
        alertTypeParams,
      );
    } else {
      alertTypeUpdate = this.usersService.updateUserSettings(
        this.profile.id.toString(),
        alertTypeParams,
        this.alertSettings.type.meta.id,
      );
    }

    if (this.alertSettings.radius.value !== this.alertsForm.value.radius) {
      radiusUpdate = this.usersService.updateCurrentUser({
        radius: alertFormValues.radius,
      });
    }

    const updates = [alertLocationUpdate, alertTypeUpdate];

    if (radiusUpdate) {
      updates.push(radiusUpdate);
    }

    this.alertsForm.disable();

    forkJoin(updates).subscribe({
      complete: () => {
        this.getAlerts();
        this.alertsForm.enable();
        this.closeModal();
      },
    });
  }

  public updatePassword(isOpen: boolean): void {
    this.isUpdatingPassword = isOpen;

    if (this.isUpdatingPassword) {
      this.updatePasswordFields.nativeElement.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => {
        this.updatePasswordFields.nativeElement.scrollIntoView({ behavior: 'smooth' });
      }, 350);
    }

    if (this.isUpdatingPassword) {
      this.setFieldsValidators(
        [this.profileForm.controls['password'], this.profileForm.controls['confirmPassword']],
        [Validators.required, Validators.minLength(8), Validators.maxLength(64)],
      );
    } else {
      this.setFieldsValidators(
        [this.profileForm.controls['password'], this.profileForm.controls['confirmPassword']],
        [],
      );
      this.profileForm.patchValue({ password: '', confirmPassword: '' });
    }
  }

  private setFieldsValidators(controls: AbstractControl[], validators: ValidatorFn[]): void {
    controls.map((control) => {
      control.setValidators(validators);
      control.updateValueAndValidity();
    });
  }

  public getTabInformation(): void {
    this.isLoading = true;
    switch (this.selectedTabIndex) {
      case 0:
        this.getProfile();
        break;

      case 1:
        if (this.enableNotificationSettings) {
          this.getContacts();
          this.getNotifications();
        } else {
          this.getLocation();
        }
        break;

      case 2:
        if (this.enableNotificationSettings) {
          this.getLocation();
        } else {
          this.getAlerts();
        }
        break;

      case 3:
        if (this.enableNotificationSettings) {
          this.getAlerts();
        }
        break;
    }
  }

  public applyChanges(): void {
    switch (this.selectedTabIndex) {
      case 0:
        this.updateProfile();
        break;

      case 1:
        if (this.enableNotificationSettings) {
          this.updateContacts();
        } else {
          this.updateLocation();
        }
        break;

      case 2:
        if (this.enableNotificationSettings) {
          this.updateLocation();
        } else {
          this.updateAlerts();
        }
        break;

      case 3:
        if (this.enableNotificationSettings) {
          this.updateAlerts();
        }
        break;
    }
  }

  public async deleteContact(id: number): Promise<void> {
    const contactToBeDeleted = this.contacts.find((contact) => contact.id == id);
    if (!contactToBeDeleted?.can_notify) {
      this.contactsService.delete(id).subscribe({
        next: () => {
          this.getContacts();
        },
      });
    } else {
      const dialogRef = this.dialog.open(DeleteContactModalComponent, {
        width: '100%',
        maxWidth: 576,
        panelClass: ['modal', 'select-languages-modal'],
        data: {
          contactId: id,
          contactType: contactToBeDeleted.type,
          contacts: this.contacts,
        },
      });

      dialogRef.afterClosed().subscribe((changedContacts: ContactsInterface[]) => {
        if (!changedContacts) return;

        // TODO: This should be a batch update.
        const requests = changedContacts.map((contact) =>
          this.contactsService.update(contact.id, contact),
        );
        requests.push(this.contactsService.delete(id));

        forkJoin(requests).subscribe({
          next: () => {
            this.getContacts();
          },
        });
      });
    }
  }

  public addAccount(): void {
    if (this.addAccountForm.invalid) return;

    this.addAccountForm.disable();
    this.contactsService
      .post({
        active: true,
        can_notify: true,
        contact: this.addAccountForm.value.name,
        type: this.addAccountForm.value.type,
      })
      .subscribe({
        next: () => {
          this.getContacts();
          this.addAccountForm.reset();
          this.addAccountForm.markAsPristine();
          this.addAccountForm.enable();
          this.initAccountForm();
        },
      });
  }

  private closeModal(): void {
    this.matDialogRef.close();
  }

  public accountFormatChanged(event: MatRadioChange): void {
    const value: AccountTypeEnum = event.value;

    const actions = {
      [AccountTypeEnum.Email]: () => [
        Validators.required,
        Validators.pattern(regexHelper.emailValidate()),
      ],
      [AccountTypeEnum.Phone]: () => [
        Validators.required,
        Validators.pattern(regexHelper.phonePattern()),
      ],
    };

    if (actions[value]) {
      const validators = actions[value]();
      this.setFieldsValidators([this.addAccountForm.controls['name']], validators);
    }
  }

  private getNotifications(): void {
    this.notificationsService.get().subscribe({
      next: (response) => {
        this.notifications = response.results;

        this.notifications.map((notification) => {
          this.collectionsService.getById(String(notification.set_id)).subscribe({
            next: (res) => {
              this.applyNotificationName(
                res.result.id,
                res.result.name,
                NotificationTypeEnum.Collection,
              );
            },
            error: () => {
              this.savedsearchesService.getById(String(notification.set_id)).subscribe({
                next: (res) => {
                  this.applyNotificationName(
                    res.result.id!,
                    res.result.name!,
                    NotificationTypeEnum.SavedSearch,
                  );
                },
              });
            },
          });
        });
      },
    });
  }

  private applyNotificationName(
    notificationId: number,
    name: string,
    type: NotificationTypeEnum,
  ): void {
    const notification = this.notifications.find((n) => n.set_id === notificationId);
    if (notification) {
      notification.type = type;
      notification.set_name = name;
    }
  }

  public async deleteNotification(id: string): Promise<void> {
    const confirmed = await this.confirmModalService.open({
      title: this.translate.instant(
        'notification.are_you_sure_you_want_to_remove_this_notification',
      ),
      description: this.translate.instant('notify.default.proceed_warning'),
    });
    if (!confirmed) return;

    this.notificationsService.delete(id).subscribe({
      next: async () => {
        this.getNotifications();

        await this._snackBar.open(
          this.translate.instant('notification.removed'),
          this.translate.instant('message.button.default'),
        );
      },
    });
  }

  public filteredNotifications(type: NotificationTypeEnum): AccountNotificationsInterface[] {
    return this.notifications.filter((notification) => notification.type === type);
  }

  public validateContact(contact: ContactsInterface) {
    this.isContactsChanged = true;
    let error: string | null = null;

    if (contact.type === 'email') {
      const emailPattern = new RegExp(regexHelper.emailValidate());
      if (!emailPattern.test(contact.contact)) {
        error = 'Invalid Email';
      }
    }

    if (contact.type === 'phone') {
      const phonePattern = new RegExp(regexHelper.phonePattern());
      if (!phonePattern.test(contact.contact)) {
        error = 'Invalid Phone';
      }
    }

    this.errorMap[contact.id] = error ? error : null;
  }

  public checkContactsErrors(): boolean {
    return Object.values(this.errorMap).some((error) => error !== null);
  }

  public isAlertLocationOptionDisabled(alertLocation: string): boolean {
    // TODO: Re-enable when alerts should be re-enabled
    // return !this.profile.zip_code && alertLocation === AlertLocationEnum.Radius;

    return alertLocation !== AlertLocationEnum.None;
  }

  public isButtonDisabled(): boolean {
    if (this.isLoading) {
      return true;
    }

    if (this.selectedTabIndex === 0) {
      return this.profileForm.invalid || this.profileForm.pristine || this.profileForm.disabled;
    }

    if (this.enableNotificationSettings && this.selectedTabIndex === 1) {
      return this.checkContactsErrors();
    }

    if (!this.enableNotificationSettings && this.selectedTabIndex === 1) {
      return this.profileForm.get('zip_code')?.hasError('invalidZip') || this.isLoadingZip;
    }

    return false;
  }

  private getZipCode(lat: number, lng: number) {
    this.isLoadingZip = true;
    this.cdr.detectChanges();
    this.searchService.getZipcode(lat, lng).subscribe({
      next: (response: SearchZipcodeResponse) => {
        const zipCode = response.address?.postcode;
        if (zipCode) {
          this.isLoadingZip = false;
          this.profileForm.controls['zip_code'].setValue(zipCode);
        } else {
          this.profileForm.controls['zip_code'].setValue('');
        }
        this.isLoadingZip = false;
        this.cdr.detectChanges();
      },
    });
  }

  private getCoordsFromZipcode(zipcode: string) {
    if (zipcode === '') return;
    this.isLoadingZip = true;
    this.searchService.getCoordsFromZipcode(zipcode, this.zipCountryCode).subscribe({
      next: (response: SearchCoordsResponse[]) => {
        if (response.length) {
          this.currentLocation = { lat: response[0].lat, lng: response[0].lon };
          this.locationSelectComponent?.updateMarkerLocation(this.currentLocation);
          this.profileForm.controls['zip_code'].setErrors(null);
        } else {
          // Reset coords if nothing found
          this.currentLocation = { lat: '', lng: '' };
          this.profileForm.controls['zip_code'].setErrors({ invalidZip: true });
        }
        this.isLoadingZip = false;
      },
      error: () => {
        this.isLoadingZip = false;
        this.profileForm.controls['zip_code'].setErrors({ invalidZip: true });
      },
    });
  }

  public changeLocation(data: any) {
    if (this.locationSelectComponent?.wasClicked) {
      const { location, error } = data;
      const { lat, lng } = location;
      this.currentLocation = { lat, lng };
      this.emptyLocation = error;
      this.cdr.detectChanges();
      this.getZipCode(lat, lng);
    }
  }
}
